/**
 * NotificationWidget Store - Zustand store with individual selectors
 * Manages notification data, badge count, and dropdown open state
 */

import { create } from 'zustand';
import type { NotificationItem } from '@/application/queries/notifications';

// ============================================================================
// Store State
// ============================================================================

interface NotificationWidgetState {
  // Data
  notifications: NotificationItem[];
  newCount: number;
  loading: boolean;
  error: Error | null;
  hasBeenOpened: boolean;

  // Actions
  setData: (notifications: NotificationItem[], newCount: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  markAsOpened: () => void;
  _reset: () => void;
}

// Empty array constant to prevent infinite re-renders
const EMPTY_NOTIFICATIONS: NotificationItem[] = [];

const initialState = {
  notifications: EMPTY_NOTIFICATIONS,
  newCount: 0,
  loading: false,
  error: null,
  hasBeenOpened: false,
};

// ============================================================================
// Store
// ============================================================================

const useNotificationWidgetStore = create<NotificationWidgetState>((set) => ({
  ...initialState,

  setData: (notifications, newCount) =>
    set({ notifications, newCount, loading: false, error: null, hasBeenOpened: false }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  markAsOpened: () => set({ hasBeenOpened: true }),
  _reset: () => set(initialState),
}));

// ============================================================================
// Individual Selector Hooks
// ============================================================================

// Data selectors
export const useNotifications = () => useNotificationWidgetStore((s) => s.notifications);
export const useNewNotificationCount = () => useNotificationWidgetStore((s) => s.newCount);
export const useHasBeenOpened = () => useNotificationWidgetStore((s) => s.hasBeenOpened);

// State selectors
export const useNotificationLoading = () => useNotificationWidgetStore((s) => s.loading);
export const useNotificationError = () => useNotificationWidgetStore((s) => s.error);

// Action selectors
export const useSetNotificationData = () => useNotificationWidgetStore((s) => s.setData);
export const useSetNotificationLoading = () => useNotificationWidgetStore((s) => s.setLoading);
export const useSetNotificationError = () => useNotificationWidgetStore((s) => s.setError);
export const useMarkNotificationsOpened = () =>
  useNotificationWidgetStore((s) => s.markAsOpened);

// Derived selector: effective badge count (0 if opened)
export const useBadgeCount = () =>
  useNotificationWidgetStore((s) => (s.hasBeenOpened ? 0 : s.newCount));

// For testing only
export const _resetNotificationWidgetStore = () => useNotificationWidgetStore.getState()._reset();
